const keyMap: { [key: string]: string } = {
  filters: "a",
  available: "b",
  type: "c",
  brands: "d",
  vendors: "e",
  regions: "f",
  continents: "g",
  price: "h",
  currency: "i",
  sort: "j",
  id: "k",
  size: "l",

  included: "m",
  label: "n",
  value: "o",
  state: "p",
  region: "q",
  excludeRelists: "r",
  excludeAuctions: "s",
  daysBack: "t",
};

const reverseKeyMap: { [key: string]: string } = Object.fromEntries(
  Object.entries(keyMap).map(([orig, short]) => [short, orig])
);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);

type MigratedFilterValue = Record<string, unknown> & {
  id?: number;
  value?: string | number;
  state?: "include" | "exclude";
  included?: boolean;
};

type MigratedFilters = Record<string, MigratedFilterValue[]>;
type MigratedFilterMap<T extends Record<string, unknown>> = {
  [K in keyof T]: T[K] extends Array<infer U> ? Array<U & MigratedFilterValue> : T[K];
};

export const compressFilters = <T>(obj: T): T => {
  if (Array.isArray(obj)) {
    return obj.map((item) => compressFilters(item)) as T;
  } else if (isRecord(obj)) {
    const newObj: Record<string, unknown> = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const shortKey = keyMap[key] || key;
        newObj[shortKey] = compressFilters(obj[key]);
      }
    }
    return newObj as T;
  }
  return obj;
};

// Recursively decompress an object by replacing compact keys with the original ones
export const decompressFilters = <T>(obj: T): T => {
  if (Array.isArray(obj)) {
    return obj.map((item) => decompressFilters(item)) as T;
  } else if (isRecord(obj)) {
    const newObj: Record<string, unknown> = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const originalKey = reverseKeyMap[key] || key;
        newObj[originalKey] = decompressFilters(obj[key]);
      }
    }
    return newObj as T;
  }
  return obj;
};

/**
 * Runtime shape check for a decompressed ActiveFilters object. Needed at
 * boundaries that hand this app untrusted or externally-sourced data (e.g.
 * a publicly shared filter link) — every write path elsewhere just casts
 * with `as unknown as ActiveFilters`, which enforces nothing at runtime and
 * lets a malformed payload reach global state as `undefined` or garbage.
 *
 * Every category (brands, vendors, price, ...) is an array — except `id`,
 * a numeric timestamp `updateActiveFilter`/`saveFilters` stash directly on
 * the filters object itself (see `ActiveFiltersWithId` in filtersStore.ts)
 * to correlate a newly-created filter for auto-save. A real, currently-saved
 * filter can carry this key, so rejecting any non-array value here rejected
 * legitimate shared filters, not just malformed ones.
 */
export const isValidActiveFilters = (value: unknown): value is ActiveFilters =>
  isRecord(value) &&
  Object.entries(value).every(([key, v]) =>
    key === "id" ? typeof v === "number" : Array.isArray(v)
  );

/**
 * Migrates old filter format to new standardized format
 * Ensures all filters have proper id field and consistent structure
 */
export function migrateFilterFormat<T extends Record<string, unknown>>(
  filters: T
): MigratedFilterMap<T> & MigratedFilters;
export function migrateFilterFormat<T>(filters: T): T;
export function migrateFilterFormat(filters: unknown): unknown {
  if (!filters || typeof filters !== "object") {
    return filters;
  }

  // Import filter constants for ID mapping
  const sortValueToId: Record<string, number> = {
    newest: 1,
    oldest: 2,
    lowest: 3,
    highest: 4,
    relevance: 5,
  };

  const currencyValueToId: Record<string, number> = {
    USD: 1,
    EUR: 2,
    CAD: 3,
    GBP: 4,
    AUD: 5,
    NZD: 6,
    SEK: 7,
    DKK: 8,
  };

  const availableValueToId: Record<string | number, number> = {
    1: 1,
    0: 2,
  };

  const migratedFilters: Record<string, unknown> = { ...(filters as Record<string, unknown>) };

  // Process each filter category
  Object.keys(migratedFilters).forEach((filterKey) => {
    const filterArray = migratedFilters[filterKey];

    if (Array.isArray(filterArray)) {
      migratedFilters[filterKey] = filterArray.map((filter: unknown) => {
        if (!filter || typeof filter !== "object") {
          return filter;
        }

        const migratedFilter: Record<string, unknown> = {
          ...(filter as Record<string, unknown>),
        };
        const value = migratedFilter.value;

        // Map known values to their correct IDs
        if (filterKey === "sort" && typeof value === "string" && sortValueToId[value]) {
          migratedFilter.id = sortValueToId[value];
        } else if (
          filterKey === "currency" &&
          typeof value === "string" &&
          currencyValueToId[value]
        ) {
          migratedFilter.id = currencyValueToId[value];
        } else if (
          filterKey === "available" &&
          (typeof value === "string" || typeof value === "number") &&
          availableValueToId[value] !== undefined
        ) {
          migratedFilter.id = availableValueToId[value];
        } else if (migratedFilter.id === undefined && value !== undefined) {
          // Fallback: generate id from value if no mapping exists
          if (typeof value === "number") {
            migratedFilter.id = value;
          } else if (typeof value === "string") {
            const numericValue = parseInt(value, 10);
            if (!isNaN(numericValue)) {
              migratedFilter.id = numericValue;
            } else {
              // Generate hash-like id for string values
              migratedFilter.id = value
                .split("")
                .reduce((acc: number, char: string) => acc + char.charCodeAt(0), 0);
            }
          }
        }

        // Ensure backward compatibility - if only id exists, copy to value
        if (migratedFilter.value === undefined && migratedFilter.id !== undefined) {
          migratedFilter.value = migratedFilter.id;
        }

        // Normalize state field (ensure it's "include" or "exclude", default to "include")
        if (migratedFilter.included !== undefined) {
          migratedFilter.state = migratedFilter.included ? "include" : "exclude";
          // Keep included for backward compatibility but prefer state
        } else if (!migratedFilter.state) {
          migratedFilter.state = "include";
        }

        return migratedFilter;
      });
    }
  });

  return migratedFilters;
}
